import { useEffect } from 'react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { useLanguage } from '../../context/LanguageContext';
import { Card, CardContent } from '../../components/ui/card';
import { Heart, Globe, Users, HandHeart, ExternalLink } from 'lucide-react';
import { Button } from '../../components/ui/button';
import usePageBanner from '../../hooks/usePageBanner';
import { AutoTranslate } from '../../hooks/useAutoTranslate';

const CaritasPage = () => {
  const { t, language, isRTL } = useLanguage();
  const { bannerImage, bannerTitle, bannerSubtitle } = usePageBanner('caritas', language);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen themed-page" dir={isRTL ? 'rtl' : 'ltr'}>
      <Navbar />

      {/* Header */}
      <section className="themed-hero relative h-80 overflow-hidden">
        <img
          src={bannerImage || "https://images.unsplash.com/photo-1593113598332-cd288d649433?w=1920"}
          alt="Caritas"
          className="w-full h-full object-cover"
        />
        <div className={`absolute inset-0 bg-gradient-to-r ${isRTL ? 'from-black/40 to-black/70' : 'from-black/70 to-black/40'} flex items-center`}>
          <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full ${isRTL ? 'text-right' : ''}`}>
            <h1 className="responsive-hero-title font-serif font-bold text-white mb-4 fade-in">
              {bannerTitle || 'Caritas Sverige'}
            </h1>
            <p className="text-xl text-gray-200 fade-in">
              {bannerSubtitle || <AutoTranslate>Katolska kyrkans biståndsorganisation</AutoTranslate>}
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Introduction */}
          <Card className="border-0 shadow-xl card-hover mb-8">
            <CardContent className="p-8">
              <div className={`flex items-start gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Heart className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4">
                    <AutoTranslate>Om Caritas</AutoTranslate>
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    <AutoTranslate>
                      Caritas Sverige är den katolska kyrkans biståndsorganisation och ingår i Caritas Internationalis - ett av världens största biståndsnätverk som förenar drygt 160 katolska nationella biståndsorganisationer i över 200 länder.
                    </AutoTranslate>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Local Work */}
          <h2 className="text-3xl font-serif font-bold text-gray-900 mb-6 text-center">
            <AutoTranslate>Verksamhet i Sverige</AutoTranslate>
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* Local Support */}
            <Card className="border-0 shadow-xl card-hover hover:shadow-xl transition-shadow">
              <CardContent className="p-6">
                <div className={`flex items-center gap-3 mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <Users className="w-5 h-5 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-serif font-bold text-gray-900">
                    <AutoTranslate>Lokalt stöd</AutoTranslate>
                  </h3>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  <AutoTranslate>
                    I Sverige arbetar Caritas med lokalt stöd till äldre och sjuka, rådgivning i asyl- och migrationsfrågor och är engagerad i arbetet mot trafficking.
                  </AutoTranslate>
                </p>
              </CardContent>
            </Card>

            {/* Mötesplats Caritas */}
            <Card className="border-0 shadow-xl card-hover hover:shadow-xl transition-shadow">
              <CardContent className="p-6">
                <div className={`flex items-center gap-3 mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <HandHeart className="w-5 h-5 text-green-600" />
                  </div>
                  <h3 className="text-xl font-serif font-bold text-gray-900">
                    <AutoTranslate>Mötesplats Caritas</AutoTranslate>
                  </h3>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  <AutoTranslate>
                    I Stockholm och Malmö driver Caritas Sverige "Mötesplats Caritas". Målet är att skapa en trygg miljö där utsatta människor kan få rådgivning, information och medmänskligt stöd.
                  </AutoTranslate>
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Global Work */}
          <Card className="border-0 shadow-xl card-hover mb-8 bg-gradient-to-r from-blue-50 to-purple-50">
            <CardContent className="p-8">
              <div className={`flex items-start gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Globe className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4">
                    <AutoTranslate>Internationellt arbete</AutoTranslate>
                  </h2>
                  <p className="text-gray-700 leading-relaxed">
                    <AutoTranslate>
                      I världen genomför Caritas humanitära insatser i katastrofområden samt stöder länder i deras kamp mot fattigdom och förtryck.
                    </AutoTranslate>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Volunteer */}
          <Card className="border-0 shadow-xl card-hover mb-8 bg-gradient-to-r from-green-500 to-emerald-600 text-white">
            <CardContent className="p-8 text-center">
              <h2 className="text-2xl font-serif font-bold mb-4">
                <AutoTranslate>Bli volontär</AutoTranslate>
              </h2>
              <p className="text-green-100 leading-relaxed mb-6">
                <AutoTranslate>
                  Caritas välkomnar volontärinsatser av olika slag. Är du intresserad av att bli volontär kan du skicka in en intresseanmälan.
                </AutoTranslate>
              </p>
              <a 
                href="https://www.caritas.se/nyhetsarkiv/2023/mars/intresseanmalan-volontar/" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button variant="secondary" className={`flex items-center gap-2 mx-auto ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <ExternalLink className="w-4 h-4" />
                  <AutoTranslate>Anmäl intresse</AutoTranslate>
                </Button>
              </a>
            </CardContent>
          </Card>

          {/* Donate */}
          <Card className="border-0 shadow-xl card-hover bg-gradient-to-r from-red-500 to-orange-500 text-white">
            <CardContent className="p-8 text-center">
              <h2 className="text-2xl font-serif font-bold mb-4">
                <AutoTranslate>Stöd Caritas</AutoTranslate>
              </h2>
              <p className="text-red-100 leading-relaxed mb-6">
                <AutoTranslate>
                  För insamling till Romersk-katolska kyrkans karitativa arbete som bedrivs av Caritas.
                </AutoTranslate>
              </p>
              <div className="grid sm:grid-cols-2 gap-4 max-w-md mx-auto">
                <div className="bg-white/20 rounded-lg p-4">
                  <p className="text-sm text-red-100 mb-1">Bankgiro</p>
                  <p className="text-xl font-bold">900-4789</p>
                </div>
                <div className="bg-white/20 rounded-lg p-4">
                  <p className="text-sm text-red-100 mb-1">Swish</p>
                  <p className="text-xl font-bold">900 4789</p>
                </div>
              </div>
              <div className="mt-6">
                <a 
                  href="https://caritas.se/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <Button variant="secondary" className={`flex items-center gap-2 mx-auto ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <ExternalLink className="w-4 h-4" />
                    <AutoTranslate>Besök caritas.se</AutoTranslate>
                  </Button>
                </a>
              </div>
            </CardContent>
          </Card>

        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CaritasPage;
